// components/Header.tsx
import { Fragment } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { useNavigate } from 'react-router-dom'
import { useUIStore } from '@/store/uiStore'
import { useAuthStore } from '@/store/authStore'
import {
  Bars3Icon,
  BellIcon,
  MagnifyingGlassIcon,
  UserCircleIcon,
  CogIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'

export default function Header() {
  const navigate = useNavigate()
  const { sidebarOpen, setSidebarOpen } = useUIStore()
  const { user, logout } = useAuthStore()

  return (
    <header className="flex items-center justify-between h-16 px-4 bg-white border-b border-gray-200">
      {/* 左侧：菜单按钮 */}
      <div className="flex items-center">
        <button
          type="button"
          className="lg:hidden p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          <Bars3Icon className="w-6 h-6" />
        </button>
      </div>

      {/* 右侧：搜索框 + 铃铛 + 用户菜单 */}
      <div className="flex items-center space-x-4">
        {/* 搜索框 */}
        <div className="hidden sm:block">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="搜索用户、订单..."
              className="block w-64 pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
        </div>

        {/* 铃铛 */}
        <button
          type="button"
          className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors relative"
        >
          <BellIcon className="w-6 h-6" />
          <span className="absolute top-1 right-1 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-white" />
        </button>

        {/* 用户菜单 */}
        <Menu as="div" className="relative">
          <Menu.Button className="flex items-center space-x-3 p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors">
            <div className="hidden sm:block text-right">
              <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
              <p className="text-xs text-gray-500">
                {user?.role === 'admin' ? '系统管理员' :
                 user?.role === 'finance' ? '财务人员' : '审计人员'}
              </p>
            </div>
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-blue-600 font-medium text-sm">
                {user?.full_name?.charAt(0).toUpperCase()}
              </span>
            </div>
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <Menu.Item>
                {({ active }: { active: boolean }) => (
                  <button
                    onClick={() => navigate('/profile')}
                    className={clsx(
                      'flex w-full items-center px-4 py-2 text-sm text-gray-700',
                      active && 'bg-gray-100'
                    )}
                  >
                    <UserCircleIcon className="mr-3 h-5 w-5 text-gray-400" />
                    个人资料
                  </button>
                )}
              </Menu.Item>

              <Menu.Item>
                {({ active }: { active: boolean }) => (
                  <button
                    onClick={() => navigate('/profile?tab=password')}
                    className={clsx(
                      'flex w-full items-center px-4 py-2 text-sm text-gray-700',
                      active && 'bg-gray-100'
                    )}
                  >
                    <CogIcon className="mr-3 h-5 w-5 text-gray-400" />
                    账户设置
                  </button>
                )}
              </Menu.Item>

              <div className="border-t border-gray-100" />

              <Menu.Item>
                {({ active }: { active: boolean }) => (
                  <button
                    onClick={logout}
                    className={clsx(
                      'flex w-full items-center px-4 py-2 text-sm text-gray-700',
                      active && 'bg-gray-100'
                    )}
                  >
                    <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-gray-400" />
                    退出登录
                  </button>
                )}
              </Menu.Item>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    </header>
  )
}
